import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Copy, KeyRound, RefreshCw, Trash2, X } from "lucide-react";
import { appsApi } from "@/api/apps";
import { functionsApi } from "@/api/functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DetailSkeleton } from "@/components/detail-skeleton";
import { TriggerBadge } from "./_authed.functions.index";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { formatRelative } from "@/lib/format";
import { useState } from "react";

export const Route = createFileRoute("/_authed/apps/$id")({
  component: AppDetailPage,
});

/** One-time reveal dialog for a newly-rotated signing key. */
function RotateRevealDialog({
  open,
  onClose,
  signingKey,
}: {
  open: boolean;
  onClose: () => void;
  signingKey: string | null;
}) {
  const [copied, setCopied] = useState(false);

  if (!signingKey) return null;

  function handleCopy() {
    if (!signingKey) return;
    void navigator.clipboard.writeText(signingKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>New signing key — save it now</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                This is the only time the full signing key is shown. Copy it and update your SDK
                configuration. The previous key remains valid for 24 hours.
              </p>
              <div className="flex items-center gap-2 rounded-md border bg-muted p-2 font-mono text-xs break-all">
                <span className="flex-1 select-all">{signingKey}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="shrink-0"
                  onClick={handleCopy}
                  aria-label="Copy new signing key"
                >
                  <Copy size={14} />
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button onClick={onClose}>Done — I copied the key</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AppDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const nav = useNavigate();

  const appQ = useQuery({
    queryKey: ["app", id],
    queryFn: () => appsApi.get(id),
  });
  const fnsQ = useQuery({
    queryKey: ["functions", id],
    queryFn: () => functionsApi.list({ app_id: id }),
  });

  const [syncOpen, setSyncOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [syncResult, setSyncResult] = useState<{
    synced_functions: number;
    archived_functions: number;
  } | null>(null);

  const [rotateRevealKey, setRotateRevealKey] = useState<string | null>(null);
  const [rotateRevealOpen, setRotateRevealOpen] = useState(false);

  const sync = useMutation({
    mutationFn: () => appsApi.sync(id),
    onSuccess: (result) => {
      setSyncOpen(false);
      setSyncResult(result);
      void qc.invalidateQueries({ queryKey: ["app", id] });
      void qc.invalidateQueries({ queryKey: ["functions", id] });
    },
  });

  const rotate = useMutation({
    mutationFn: () => appsApi.rotateSigningKey(id),
    onSuccess: (result) => {
      setRotateOpen(false);
      void qc.invalidateQueries({ queryKey: ["app", id] });
      setRotateRevealKey(result.signing_key_current);
      setRotateRevealOpen(true);
    },
  });

  const del = useMutation({
    mutationFn: () => appsApi.del(id),
    onSuccess: () => {
      setDeleteOpen(false);
      void nav({ to: "/apps" });
    },
  });

  if (appQ.isLoading) return <DetailSkeleton />;
  if (appQ.isError || !appQ.data)
    return <div className="text-fail">App not found</div>;

  const app = appQ.data;
  const fns = fnsQ.data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <Link to="/apps" className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft size={13} /> Apps
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">{app.name}</h1>
            <div className="mt-1 font-mono text-xs text-muted-foreground">{app.sdk_url}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setSyncOpen(true)}>
              <RefreshCw size={14} /> Sync now
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setRotateOpen(true)}>
              <KeyRound size={14} /> Rotate key
            </Button>
            <Button variant="destructive" size="sm" className="gap-1.5" onClick={() => setDeleteOpen(true)}>
              <Trash2 size={14} /> Delete
            </Button>
          </div>
        </div>
      </div>

      {syncResult && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-lg border border-ok/25 bg-ok/10 px-4 py-2.5 text-sm text-ok"
        >
          <span className="flex items-center gap-2">
            <CheckCircle2 size={15} />
            Sync complete: {syncResult.synced_functions} function
            {syncResult.synced_functions !== 1 ? "s" : ""} updated,{" "}
            {syncResult.archived_functions} archived.
          </span>
          <button onClick={() => setSyncResult(null)} aria-label="Dismiss" className="text-ok/70 hover:text-ok">
            <X size={15} />
          </button>
        </div>
      )}

      <Card className="grid grid-cols-1 gap-px overflow-hidden bg-border sm:grid-cols-2">
        <div className="bg-card px-4 py-3">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Signing key</div>
          <div className="font-mono text-[13px]">
            signkey_…<span className="text-foreground">{app.signing_key_last4}</span>
            <span className="ml-2 text-[11px] text-muted-foreground/70">shown in full only once</span>
          </div>
        </div>
        <div className="bg-card px-4 py-3">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Last synced</div>
          <div className="font-mono text-[13px]">
            {app.last_synced_at ? formatRelative(app.last_synced_at) : "never"}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b px-5 py-3">
          <h2 className="font-display text-[15px] font-semibold tracking-tight">Functions</h2>
          <p className="text-xs text-muted-foreground">Discovered from this app's manifest</p>
        </div>
        {fns.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">
            No functions synced yet. Click "Sync now" to fetch the manifest from your SDK.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Slug</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Trigger</TableHead>
                <TableHead className="pr-5 font-mono text-[11px] uppercase tracking-wider">Event / Cron</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fns.map((f) => (
                <TableRow key={f.id} className="group cursor-pointer">
                  <TableCell className="pl-5">
                    <Link to="/functions/$id" params={{ id: f.id }}
                      className="font-mono text-[13px] font-medium text-foreground group-hover:text-brand">
                      {f.slug}
                    </Link>
                  </TableCell>
                  <TableCell><TriggerBadge type={f.trigger_type} /></TableCell>
                  <TableCell className="pr-5 font-mono text-[12px] text-muted-foreground">
                    {f.trigger_event ?? f.trigger_cron ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Sync confirm dialog — lighter: no destructive variant */}
      <ConfirmDialog
        open={syncOpen}
        onOpenChange={setSyncOpen}
        title="Sync manifest from SDK?"
        description="deleggate will POST a signed GET-manifest request to your SDK and reconcile the function list."
        confirmLabel="Sync now"
        loading={sync.isPending}
        onConfirm={() => sync.mutate()}
      />

      {/* Rotate signing key — confirm then reveal new key */}
      <ConfirmDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        title="Rotate signing key?"
        description="A new signing key will be generated for outbound invokes. The current key stays valid for 24 hours as fallback."
        affected={[
          "The current signing key becomes the 'previous' key for 24h",
          "Your SDK must accept both keys during the grace window",
        ]}
        confirmLabel="Rotate key"
        loading={rotate.isPending}
        onConfirm={() => rotate.mutate()}
      />

      {/* Delete app — destructive with cascade description */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this app?"
        description="This will archive all functions belonging to this app. In-flight runs continue to completion; new triggers will not match archived functions."
        affected={fns.map((f) => `Archive function ${f.slug}`)}
        confirmLabel="Delete app"
        loading={del.isPending}
        onConfirm={() => del.mutate()}
      />

      {/* One-time reveal of newly-rotated signing key */}
      <RotateRevealDialog
        open={rotateRevealOpen}
        onClose={() => {
          setRotateRevealOpen(false);
          setRotateRevealKey(null);
        }}
        signingKey={rotateRevealKey}
      />
    </div>
  );
}
